export type FeatureTarget = "page" | "widget" | "both";

const WIDGET_HOST_ID = "incluway-widget-root";
const SHADOW_STYLE_SUFFIX = "--shadow";

function findStyle(
  root: Document | ShadowRoot,
  id: string,
): HTMLStyleElement | null {
  if (root instanceof Document) {
    return root.getElementById(id) as HTMLStyleElement | null;
  }

  return root.querySelector(`#${id}`) as HTMLStyleElement | null;
}

export function getWidgetHost(): HTMLElement | null {
  return document.getElementById(WIDGET_HOST_ID);
}

export function getWidgetShadowRoot(): ShadowRoot | null {
  return getWidgetHost()?.shadowRoot ?? null;
}

export function upsertStyleTag(
  id: string,
  css: string,
  root: Document | ShadowRoot = document,
) {
  let style = findStyle(root, id);

  if (!style) {
    style = document.createElement("style");
    style.id = id;

    if (root instanceof Document) {
      root.head.appendChild(style);
    } else {
      root.appendChild(style);
    }
  }

  style.textContent = css;
}

export function removeStyleTag(
  id: string,
  root: Document | ShadowRoot = document,
) {
  findStyle(root, id)?.remove();
}

export function upsertScopedStyleTag(options: {
  id: string;
  pageCss?: string;
  widgetCss?: string;
  target?: FeatureTarget;
}) {
  const { id, pageCss, widgetCss = pageCss, target = "page" } = options;

  if (target === "page" || target === "both") {
    if (pageCss) upsertStyleTag(id, pageCss, document);
    else removeStyleTag(id, document);
  } else {
    removeStyleTag(id, document);
  }

  const shadowRoot = getWidgetShadowRoot();
  if (!shadowRoot) return;

  const shadowId = `${id}${SHADOW_STYLE_SUFFIX}`;

  if (target === "widget" || target === "both") {
    if (widgetCss) upsertStyleTag(shadowId, widgetCss, shadowRoot);
    else removeStyleTag(shadowId, shadowRoot);
  } else {
    removeStyleTag(shadowId, shadowRoot);
  }
}

export function removeScopedStyleTag(id: string) {
  removeStyleTag(id, document);

  const shadowRoot = getWidgetShadowRoot();
  if (shadowRoot) {
    removeStyleTag(`${id}${SHADOW_STYLE_SUFFIX}`, shadowRoot);
  }
}

export function getPageExcludeSelector() {
  return `:not(#${WIDGET_HOST_ID}):not(#${WIDGET_HOST_ID} *)`;
}
